import React, { useState, useMemo } from 'react';
import { message, Divider } from 'antd';
import useQuery from 'hooks/useQuery';

import { Button, Modal, SocialButton, Typography } from 'ui-kit';
import { Checkbox } from 'components/Form/Input';
import './SignUpPage.scss';
import { createOwner, newLogin, socialLoginOrCreateOwner } from '../../api/auth';
import { useHistory } from 'react-router-dom';
import Link from '../../ui-kit/Link';
import useSocialAuth from 'hooks/useSocialAuth';
import UnauthLayout from 'components/UnauthLayout';
import SignUpForm from './SignUpForm';
import { FormProvider, useForm } from 'react-hook-form';
import { signupResolver } from './utils';

import { recordLeadForTolt } from 'utils/integrations';
import _ from 'lodash';
import { URLS } from 'components/Routes/urls';
import { BASE_URL } from 'utils/sdk';

const SignUpPage = () => {
  const history = useHistory();
  const [checked, setChecked] = useState(true);
  const [loading, setLoading] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { showSocialAuthMessage } = useSocialAuth();

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      promoCode: '',
      firstName: '',
      email: '',
      password: '',
    },
    resolver: signupResolver,
  });

  const handleSubmit = () => {
    setModalIsOpen(true);
  };

  const queryParams = useQuery();

  const queryParamsString = useMemo(
    () =>
      Object.keys(queryParams)
        .map((key) => `${key}=${queryParams[key]}`)
        .join('&'),
    [queryParams],
  );

  const create = () => {
    const { firstName, email, password } = methods.getValues();
    setLoading(true);
    const payload = { firstName, email, password };
    createOwner(payload).then((result) => {
      if (result.ok) {
        window.fbq?.(
          'trackCustom',
          'CreateAccount',
          { from: 'email', email: result.data?.user?.email },
          { eventID: result?.data?.id },
        );
        newLogin(email, password).then(() => {
          recordLeadForTolt({ email });
          if (Object.keys(queryParams).length > 0) {
            const hasNextParam = _.get(queryParams, 'next', null);
            if (hasNextParam) {
              let nextParam = queryParamsString.slice(queryParamsString.indexOf('next=') + 5);

              nextParam = nextParam.startsWith('/') ? nextParam.slice(1) : nextParam;

              window.location.href = `${BASE_URL}${nextParam}`;
            }
          } else {
            history.push(URLS.ONBOARDING_VIDEO);
          }

          setLoading(false);
        });
      } else {
        setLoading(false);
        if (Object.keys(result?.errors.fields).length > 0) {
          // eslint-disable-next-line no-unsafe-optional-chaining
          const { email, ...rest } = result?.errors?.fields;

          if (
            (email && email.includes('user with this email address already exists.')) ||
            !_.isUndefined(_.get(rest, '0'))
          ) {
            showSocialAuthMessage();
            return;
          }
        }

        if (result?.errors.nonFields.length > 0) {
          message.error(result?.errors.nonFields[0]);
        }
      }
    });
  };

  const socialSubmit = (platform) => {
    socialLoginOrCreateOwner(platform);
  };

  return (
    <UnauthLayout title="Sign up for your account">
      <FormProvider {...methods}>
        <div className="SignupPage-social-login">
          <SocialButton
            social="google"
            label="Sign up with Google"
            fullWidth
            onClick={() => socialSubmit('google-oauth2')}
          />
          <SocialButton
            social="facebook"
            label=" Sign up with Facebook"
            fullWidth
            onClick={() => socialSubmit('facebook')}
          />
        </div>
        <Divider>
          <Typography variant="caption" color="textGray">
            OR
          </Typography>
        </Divider>
        <SignUpForm onSubmit={handleSubmit} />
      </FormProvider>
      <Typography variant="body2">
        Already have an account? &nbsp;
        <Button to={`/login/?${queryParamsString}`} className="SignupPage-singin-button">
          Sign in here
        </Button>
      </Typography>
      <Modal
        className="SignupPage-agreement-modal"
        visible={modalIsOpen}
        title="Complete signing up"
        onCancel={() => {
          setModalIsOpen(false);
        }}
        footer={
          <Button
            variant="contained"
            onClick={create}
            fullWidth
            disabled={!checked}
            loading={loading}
          >
            Continue to Dashboard
          </Button>
        }
      >
        <div className="SignupPage-agreement-modal__content">
          <Checkbox
            className="agreement-checkbox"
            checked={checked}
            onChange={() => {
              setChecked(!checked);
            }}
          />
          <p>
            By continuing, you agree to The Host Co.&nbsp;
            <Link href="https://www.thehost.co/terms-conditions">terms of service</Link>
            &nbsp;and&nbsp;
            <Link href="https://www.thehost.co/privacy-policy">privacy policy</Link>.
          </p>
        </div>
      </Modal>
    </UnauthLayout>
  );
};

export default SignUpPage;
